import { useState, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { trackEvent } from "../lib/analytics";
import Footer from "../components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UploadPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const categoryParam = (searchParams.get("category") || "minimalist").toLowerCase();
  const category = ["renaissance", "ipl"].includes(categoryParam) ? categoryParam : "minimalist";
  const styleId = searchParams.get("style") || null;
  const sizeId = (searchParams.get("size") || "8x10").toLowerCase();
  const couponParam = (searchParams.get("coupon") || "").toUpperCase() || null;
  const requiresPetName = category === "minimalist";
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [petName, setPetName] = useState("");
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  }, []);

  /**
   * Downscale + re-encode an image client-side so we never POST a 5–10MB
   * base64 body to the backend (which Cloudflare / K8s ingress can reject as
   * a "Network Error" before the request even hits FastAPI).
   */
  const compressImage = (file) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      const reader = new FileReader();
      reader.onerror = () => reject(new Error("Failed to read image"));
      reader.onload = () => {
        img.onerror = () => reject(new Error("Failed to decode image"));
        img.onload = () => {
          const MAX_DIM = 1600;
          let { width, height } = img;
          if (width > MAX_DIM || height > MAX_DIM) {
            const scale = Math.min(MAX_DIM / width, MAX_DIM / height);
            width = Math.round(width * scale);
            height = Math.round(height * scale);
          }
          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error("Compression failed"));
                return;
              }
              const compressed = new File([blob], "pet.jpg", {
                type: "image/jpeg",
                lastModified: Date.now(),
              });
              resolve(compressed);
            },
            "image/jpeg",
            0.85
          );
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    });

  const handleFileSelect = (file) => {
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    setSelectedFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleGenerateStyles = async () => {
    if (!selectedFile) {
      toast.error("Please upload a pet image first");
      return;
    }

    if (requiresPetName && !petName.trim()) {
      toast.error("Please enter your pet's name");
      return;
    }
    if (!styleId) {
      toast.error("No style selected. Please pick one from the homepage.");
      return;
    }

    try {
      setUploading(true);

      // Compress client-side first — phone photos are often 4–8MB which can
      // get rejected by the ingress when re-sent as base64 in /api/generate-styles.
      // Compression brings a typical 6MB photo down to ~300–500KB.
      const fileToUpload = await compressImage(selectedFile).catch((err) => {
        console.warn("Compression skipped:", err);
        return selectedFile;
      });

      // Upload image
      const formData = new FormData();
      formData.append("file", fileToUpload);

      const uploadResponse = await axios.post(`${API}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });

      const imageUrl = uploadResponse.data.image_url;
      const originalUrl = uploadResponse.data.original_url || "";

      setUploading(false);
      setGenerating(true);

      // Analytics: this user pushed "Generate" successfully — count it as
      // one generation attempt. Admin dashboard uses this for the funnel.
      trackEvent("generation_started", {
        category,
        style_id: styleId,
        pet_name: petName,
      });

      // Start async generation. We send the public Object Storage URL
      // (`original_url`) as `image_url` when available — backend will fetch
      // bytes server-side. This keeps the POST body tiny (~300 bytes) instead
      // of carrying the 6MB+ base64 data URL.
      const startResponse = await axios.post(
        `${API}/generate-styles`,
        {
          image_url: originalUrl || imageUrl,
          original_url: originalUrl,
          pet_name: petName || "Pet",
          category,
          style_id: styleId,
          size_id: sizeId,
        },
        { timeout: 30000 }
      );

      const jobId = startResponse.data.job_id;

      // Poll for results every 3 seconds
      let result = null;
      for (let i = 0; i < 80; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const pollResponse = await axios.get(
          `${API}/generate-styles/${jobId}`
        );

        if (pollResponse.data.status === "completed") {
          result = pollResponse.data;
          break;
        } else if (pollResponse.data.status === "failed") {
          throw new Error(
            pollResponse.data.error || "Generation failed"
          );
        }
        // still processing, continue polling
      }

      if (!result) {
        throw new Error("Generation timed out. Please try again.");
      }

      // Navigate to style selection with data
      navigate("/styles", {
        state: {
          // Object Storage URL only — never a data URL. If upload to storage
          // failed we'd rather store an empty string than a 2MB+ base64 blob.
          originalImage: originalUrl || "",
          styles: result.styles,
          petName: petName,
          category,
          styleId,
          selectedSize: sizeId,
          couponCode: couponParam,
          jobId,
        },
      });
    } catch (error) {
      console.error("Full error object:", error);
      console.error("Error response:", error.response);
      console.error("Error message:", error.message);
      
      let errorMessage = "Failed to generate styles. Please try again.";
      
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMessage = "This is taking longer than expected. Please try again.";
      } else if (error.message?.includes('Budget has been exceeded')) {
        errorMessage = "Your Emergent LLM Key balance is exhausted. Please top up via Profile → Universal Key → Add Balance.";
      } else if (
        error.message?.includes('502') ||
        error.message?.includes('BadGateway') ||
        error.message?.includes('Bad Gateway')
      ) {
        errorMessage = "Google Gemini is experiencing a temporary outage. Please try again in 10 to 15 minutes.";
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
      setGenerating(false);
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass-header py-5">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 flex items-center">
          <Link to="/" className="serif text-2xl tracking-tight text-[#1A1A1A]">
            theSnugglePrints
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-16">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          {/* Intro */}
          <div className="text-center mb-14">
            <p className="label-uppercase mb-4">
              {requiresPetName ? "Create your portrait" : "Upload your pet's photo"}
            </p>
            <h2 className="serif text-4xl sm:text-5xl text-[#1A1A1A] mb-5 leading-tight">
              Upload your pet's photo.
            </h2>
            <p className="text-[#6B6B6B] max-w-xl mx-auto">
              {requiresPetName
                ? "Works best with clear, well-lit pet photos. Your portrait will be ready in about 1 to 2 minutes, with your pet's name included."
                : "Works best with a clear, front-facing pet photo. We'll generate two painted variations for you to pick from in about 1 to 2 minutes."}
            </p>
          </div>

          <div className="bg-card border border-[#EAE5DF] p-8 sm:p-10 mb-6">
            <Label className="label-uppercase mb-4 block">
              Upload Pet Photo
            </Label>

            <div
              className={`dropzone flex flex-col items-center justify-center min-h-[360px] cursor-pointer ${
                dragActive ? "border-primary bg-primary/5" : ""
              }`}
              data-testid="upload-dropzone"
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => document.getElementById("fileInput").click()}
            >
              {previewUrl ? (
                <div className="relative w-full h-full flex items-center justify-center p-4">
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="max-h-[320px] max-w-full object-contain"
                    data-testid="upload-preview-image"
                  />
                  <Button
                    variant="secondary"
                    size="sm"
                    className="absolute top-4 right-4"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(null);
                      setPreviewUrl(null);
                    }}
                    data-testid="remove-image-button"
                  >
                    Remove
                  </Button>
                </div>
              ) : (
                <div className="text-center p-8">
                  <Upload className="w-12 h-12 mx-auto mb-4 text-[#6B6B6B]" />
                  <h3 className="serif text-2xl text-[#1A1A1A] mb-2">
                    Drop your pet photo here
                  </h3>
                  <p className="text-sm text-[#6B6B6B] mb-4">
                    or click to browse
                  </p>
                  <p className="text-xs text-[#6B6B6B]">
                    JPG or PNG, up to 10 MB
                  </p>
                </div>
              )}
            </div>

            <input
              id="fileInput"
              type="file"
              accept="image/*"
              onChange={handleFileInputChange}
              className="hidden"
              data-testid="file-input"
            />
          </div>

          {/* Pet Name (modern only) */}
          {previewUrl && requiresPetName && (
            <div className="bg-card border border-[#EAE5DF] p-8 sm:p-10 mb-6">
              <Label htmlFor="pet-name" className="label-uppercase mb-4 block">
                Pet Name
              </Label>
              <Input
                id="pet-name"
                type="text"
                placeholder="e.g., Raven, Buddy, Luna"
                value={petName}
                onChange={(e) => setPetName(e.target.value)}
                className="mb-3 text-lg"
                data-testid="pet-name-input"
              />
              <p className="text-sm text-[#6B6B6B]">
                This name will appear on your portrait.
              </p>
            </div>
          )}

          {/* Generate Button */}
          {previewUrl && (
            <button
              onClick={handleGenerateStyles}
              disabled={
                uploading ||
                generating ||
                (requiresPetName && !petName.trim())
              }
              className="btn-accent w-full h-14 text-base disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="generate-styles-button"
            >
              {uploading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Uploading your photo…
                </>
              ) : generating ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Creating your portrait…
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-5 w-5" />
                  Create My Pet Portrait
                </>
              )}
            </button>
          )}

          {generating && (
            <p className="text-center text-sm text-[#6B6B6B] mt-5 italic">
              This usually takes about 60 seconds. You&apos;ll see your framed portrait as soon as it&apos;s ready.
            </p>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default UploadPage;
